import { FC, useEffect, useState } from "react";
import { CarDataType } from "data/types";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import { CarListingModal } from "models/vehicleModal";
import FleetCard from "designs/DesignA/components/FleetCard/FleetCard";
import TabFilters from "designs/Common/TabFilters";
import { inAnyClass, uniqueFleetClassNames } from "utils/fleetClasses";
import { useFilterReset } from "hooks/useFilterReset";
import NoVehiclesMatch from "designs/Common/NoVehiclesMatch";
import { DESIGN_A_SORT_URL, designASortFromUrl } from "utils/fleetSort";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: CarDataType[];
}

const SectionGridFilterCard: FC<SectionGridFilterCardProps> = ({
  className = "",
  data,
}) => {
  const { setSelectedCar, availableFleet } = useData();
  const [visibleCarsCount, setVisibleCarsCount] = useState<number>(8);
  const [expanded, setExpanded] = useState<boolean>(false);
  const location = useLocation();
  // 1NOW-11228: the filters live in the URL, as on /vehicles (1NOW-11193): one class=/brand=/model=
  // per pick, price=min-max and sort=. Going to a car and coming Back finds them there.
  const queryParams = new URLSearchParams(location.search);
  const listParam = (key: string) => queryParams.getAll(key).map((v) => v.trim()).filter(Boolean);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    listParam("class").length ? listParam("class") : listParam("make")
  );
  const [selectedMakes, setSelectedMakes] = useState<string[]>(() => listParam("brand"));
  const [selectedModels, setSelectedModels] = useState<string[]>(() => listParam("model"));
  const [sortOrder, setSortOrder] = useState<string>(() => designASortFromUrl(queryParams.get("sort")));
  const [maxPrice, setMaxPrice] = useState(0);
  const [filteredFleet, setFilteredFleet] = useState<CarListingModal[]>(
    availableFleet || []
  );
  // The price range the renter picked, null until they do (then it is the whole range and follows
  // the fleet). The URL's price is the first pick, so the fleet's top price arriving can't reset it.
  const urlPrice = (queryParams.get("price") ?? "").match(/^(\d+)-(\d+)$/);
  const [pickedRange, setPickedRange] = useState<number[] | null>(
    urlPrice && Number(urlPrice[1]) <= Number(urlPrice[2]) ? [Number(urlPrice[1]), Number(urlPrice[2])] : null
  );
  const rangePrices = pickedRange ?? [0, maxPrice];
  const setRangePrices = (v: number[]) => setPickedRange(v[0] <= 0 && v[1] >= maxPrice ? null : v);
  const priceMoved = pickedRange !== null && (maxPrice === 0 || pickedRange[0] > 0 || pickedRange[1] < maxPrice);
  const navigate = useNavigate();
  // 1NOW-11132: "Clear filters" and the no-match message.
  const { filtersActive, filtersKey, pickPrices, clearFilters } = useFilterReset({
    selectedTypes,
    selectedMakes,
    selectedModels,
    setSelectedTypes,
    setSelectedMakes,
    setSelectedModels,
    setRangePrices,
    maxPrice,
  });

  useEffect(() => {
    if (availableFleet && availableFleet.length > 0) {
      const calculatedMaxPrice = availableFleet.reduce(
        (acc, current) => Math.max(acc, current.avg_car_price_per_day ?? 0),
        0
      );
      setMaxPrice(calculatedMaxPrice);
    }
  }, [availableFleet]);

  // Every Apply is written back, replaced, not pushed: Back leaves the page. The URL is not followed
  // the other way: a new search from the bar lands on a bare /available-car, and the filters on
  // screen stay, as they always have, and are written into it again. Other params are left alone.
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    (["make", "class", "brand", "model"] as const).forEach((key) => next.delete(key));
    selectedTypes.forEach((v) => next.append("class", v));
    selectedMakes.forEach((v) => next.append("brand", v));
    selectedModels.forEach((v) => next.append("model", v));
    if (DESIGN_A_SORT_URL[sortOrder]) next.set("sort", DESIGN_A_SORT_URL[sortOrder]);
    else next.delete("sort");
    if (priceMoved) next.set("price", `${rangePrices[0]}-${rangePrices[1]}`);
    else next.delete("price");
    const search = next.toString();
    if (search !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, selectedTypes, selectedMakes, selectedModels, sortOrder, priceMoved, rangePrices[0], rangePrices[1]]);

  const getUniqueFleetClasses = () => uniqueFleetClassNames(availableFleet);

  const getUniqueMakes = () => {
    return Array.from(
      new Set(availableFleet?.map((car) => car?.make ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  const getUniqueModels = () => {
    return Array.from(
      new Set(availableFleet?.map((car) => car?.model ?? "") || [])
    ).filter((item): item is string => item !== undefined && item !== "");
  };

  // Check if any vehicles have make data
  const hasMakeData = () => {
    return availableFleet?.some((car) => car?.make && car.make !== "") || false;
  };

  // Check if any vehicles have model data
  const hasModelData = () => {
    return availableFleet?.some((car) => car?.model && car.model !== "") || false;
  };

  useEffect(() => {
    if (availableFleet) {
      let filtered = [...availableFleet];
      filtered = filtered.filter(
        (item) =>
          (item.avg_car_price_per_day ?? 0) >= rangePrices[0] &&
          (item.avg_car_price_per_day ?? 0) <= rangePrices[1]
      );

      if (
        selectedTypes.length > 0 &&
        selectedTypes.length < getUniqueFleetClasses().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          inAnyClass(item, selectedTypes)
        );
      }
      if (
        selectedMakes.length > 0 &&
        selectedMakes.length < getUniqueMakes().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedMakes.includes(item?.make ?? "")
        );
      }

      if (
        selectedModels.length > 0 &&
        selectedModels.length < getUniqueModels().length
      ) {
        filtered = filtered.filter((item: CarListingModal) =>
          selectedModels.includes(item?.model ?? "")
        );
      }

      if (sortOrder === "aToZ") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } else if (sortOrder === "zToA") {
        filtered.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameB.localeCompare(nameA);
        });
      } else if (sortOrder === "priceLowToHigh") {
        filtered.sort(
          (a, b) =>
            (a.avg_car_price_per_day ?? 0) - (b.avg_car_price_per_day ?? 0)
        );
      } else if (sortOrder === "priceHighToLow") {
        filtered.sort(
          (a, b) =>
            (b.avg_car_price_per_day ?? 0) - (a.avg_car_price_per_day ?? 0)
        );
      }

      setFilteredFleet(filtered);

      if (filtered.length <= visibleCarsCount) {
        setExpanded(false);
      }
    }
  }, [
    availableFleet,
    selectedTypes,
    selectedMakes,
    selectedModels,
    rangePrices[0],
    rangePrices[1],
    sortOrder,
  ]);

  const renderCard = (car: CarListingModal) => {

    return (
      <FleetCard
        key={car.id}
        data={car}
        onClick={() => {
          navigate("/listing-car-detail?selectedCarId=" + car.id);
          setSelectedCar(car);
        }}
      />
    );
  };

  return (
    <div
      className={`nc-SectionGridFilterCard ${className}`}
      style={{ minHeight: "80vh" }}
      data-nc-id="SectionGridFilterCard"
    >
      <div className="flex lg:flex-row flex-col justify-between">
        <div className={`mb-4 lg:mb-16`}>
          <h2 className="text-2xl md:text-4xl font-bold text-text-color">
            {"Available Fleet (" + filteredFleet?.length + ")"}
          </h2>
        </div>

        <div className="mb-12 lg:mb-12">
          <TabFilters
            key={filtersKey}
            types={[...getUniqueFleetClasses()]}
            makes={hasMakeData() ? [...getUniqueMakes()] : []}
            models={hasModelData() ? [...getUniqueModels()] : []}
            selectedTypes={selectedTypes}
            selectedMakes={selectedMakes}
            selectedModels={selectedModels}
            setSelectedTypes={setSelectedTypes}
            setSelectedMakes={setSelectedMakes}
            setSelectedModels={setSelectedModels}
            rangePrices={rangePrices}
            setRangePrices={pickPrices}
            maxPrice={maxPrice}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            showMakeFilter={hasMakeData()}
            showModelFilter={hasModelData()}
          />
        </div>
      </div>
      <div className="nc-SectionGridFeaturePlaces relative">
        <div
          className={`items-stretch grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}
        >
          {(filtersActive || priceMoved) && (availableFleet?.length ?? 0) > 0 && filteredFleet.length === 0 ? (
            <NoVehiclesMatch onClear={clearFilters} />
          ) : (
            filteredFleet?.map(
              (car: any) => car?.active === true && renderCard(car)
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionGridFilterCard;
